import crypto from 'node:crypto';
import fs from 'node:fs/promises';
import os from 'node:os';
import path from 'node:path';

import { isErrorOfType } from '../lib/errors.ts';
import type { WatchedFileState } from './harnesses/types.ts';

export async function watchedFileStateForPath(
  filePath: string,
): Promise<WatchedFileState> {
  try {
    const stat = await fs.stat(filePath);
    return {
      exists: true,
      size: stat.size,
      modTimeUnixMs: stat.mtime.getTime(),
    };
  } catch (error) {
    if (isErrorOfType(error, 'ENOENT')) {
      return { exists: false, size: 0, modTimeUnixMs: 0 };
    }
    throw error;
  }
}

export function watchedFileChanged(
  previous: WatchedFileState,
  current: WatchedFileState,
): boolean {
  if (previous.exists !== current.exists) return true;
  if (!previous.exists) return false;
  return (
    previous.size !== current.size ||
    previous.modTimeUnixMs !== current.modTimeUnixMs
  );
}

export async function copyFileSnapshot(sourcePath: string): Promise<{
  tempPath: string;
  md5: string;
}> {
  const tempDir = await fs.mkdtemp(path.join(os.tmpdir(), 'lore-upload-'));
  const tempPath = path.join(tempDir, path.basename(sourcePath));
  await fs.copyFile(sourcePath, tempPath);
  const bytes = await fs.readFile(tempPath);
  return {
    tempPath,
    md5: crypto.createHash('md5').update(bytes).digest('hex'),
  };
}

// Snapshot in-memory bytes into the same `{ tempPath, md5 }` shape
// `copyFileSnapshot` produces, for callers (e.g. cowork) that hold a
// transcript in memory rather than on disk. The md5 hashes the exact
// bytes written so it matches what the upload session will verify.
// Cleanup is the caller's responsibility via `removeSnapshot`.
export async function writeBufferSnapshot(
  filename: string,
  bytes: Buffer | string,
): Promise<{ tempPath: string; md5: string }> {
  const tempDir = await fs.mkdtemp(path.join(os.tmpdir(), 'lore-upload-'));
  const tempPath = path.join(tempDir, path.basename(filename));
  const buffer = typeof bytes === 'string' ? Buffer.from(bytes, 'utf8') : bytes;
  await fs.writeFile(tempPath, buffer);
  return {
    tempPath,
    md5: crypto.createHash('md5').update(buffer).digest('hex'),
  };
}

export async function removeSnapshot(tempPath: string): Promise<void> {
  await fs.rm(path.dirname(tempPath), { recursive: true, force: true });
}
